import { showNotice } from "./notice";

// Also imported by packs/view_toggle_restore.ts (the pre-paint restore pack).
export const STORAGE_KEY = "candidatesView";

const PANES_ID = "candidates-panes";
const VIEWS = ["grid", "list"];
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
// Marks the loading placeholder so it is not mistaken for real rows.
const PLACEHOLDER_ATTR = "data-view-placeholder";

const inflight: Record<string, Promise<boolean> | undefined> = {};
const needsWiring = new Set<string>();

function applyView(view: string): void {
  document
    .querySelectorAll<HTMLElement>("[data-viewtoggle] button[data-view]")
    .forEach((btn) => btn.classList.toggle("active", btn.dataset.view === view));

  document
    .querySelectorAll<HTMLElement>("[data-view-pane]")
    .forEach((pane) => {
      pane.style.display = pane.dataset.viewPane === view ? "" : "none";
    });

  // render_async_load is what the page already listens for when candidate rows
  // arrive; fire it here so a fetched pane is wired up exactly like the first one.
  if (needsWiring.delete(view)) document.dispatchEvent(new Event("render_async_load"));
  // the pane's expand icons re-measure themselves as it shows (expandable_cells.ts)
}

function persist(view: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, view);
  } catch (_e) { }
  document.cookie = `${STORAGE_KEY}=${view}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
}

const panesRoot = (): HTMLElement | null => document.getElementById(PANES_ID);

const paneFor = (view: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-view-pane="${view}"]`);

const otherView = (view: string): string => (view === VIEWS[0] ? VIEWS[1] : VIEWS[0]);

// Does this shell already hold rows? `showView` drops a spinner into the pane it
// is switching to, so a plain child count would report an empty pane as filled
// and `fetchPane` would skip the insert, leaving the spinner up for good.
function paneFilled(shell: HTMLElement): boolean {
  return Array.from(shell.children).some((el) => !el.hasAttribute(PLACEHOLDER_ATTR));
}

// #table takes the same filters as the page it is on, so the current query string
// carries them; only the pane differs.
function tableUrl(root: HTMLElement, view: string): string {
  const params = new URLSearchParams(window.location.search);
  params.set("view", view);
  return `${root.dataset.tableUrl || ""}?${params.toString()}`;
}

// Fill one pane's empty shell from #table. Resolves true once the rows are in
// place (or were already), false if the request failed.
function fetchPane(root: HTMLElement, view: string): Promise<boolean> {
  const existing = inflight[view];
  if (existing) return existing;

  const request = fetch(tableUrl(root, view), {
    headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
    credentials: "same-origin",
  })
    .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
    .then((html) => {
      // re-resolve: a Turbo visit or a filter submit may have swapped the table
      const shell = paneFor(view);
      if (!shell) return false;
      if (paneFilled(shell)) return true;

      const fresh = new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(`[data-view-pane="${view}"]`);
      if (!fresh) return false;

      shell.innerHTML = fresh.innerHTML;
      needsWiring.add(view);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inflight[view] = undefined;
    });

  inflight[view] = request;
  return request;
}

const SPINNER =
  `<div ${PLACEHOLDER_ATTR} class="text-center my-5">` +
  '<div class="spinner-border text-primary" role="status"></div></div>';

// Show `view`, fetching its rows first if they are not on the page yet.
function showView(view: string, previous?: string): void {
  const pane = paneFor(view);
  // Nothing to toggle between (e.g. the empty-result block) — leave it alone.
  if (!pane) return;

  const root = panesRoot();
  if (paneFilled(pane) || !root) {
    applyView(view);
    return;
  }

  // Prefetch has not landed yet — show the switch happening rather than freezing
  // on the old pane, and fall back to it if the request fails.
  const fallback = previous && previous !== view ? previous : "";
  pane.innerHTML = SPINNER;
  applyView(view);

  fetchPane(root, view).then((ok) => {
    if (ok) {
      applyView(view);
      return;
    }
    pane.innerHTML = "";
    if (fallback) {
      persist(fallback);
      applyView(fallback);
    }
    showNotice("Could not switch view. Please try again.", "danger");
  });
}

// Each pane is a full render of the page of candidates
function prefetchOtherPane(): void {
  const active = document.querySelector<HTMLElement>(
    "[data-viewtoggle] button[data-view].active"
  )?.dataset.view;
  const other = otherView(active && VIEWS.includes(active) ? active : VIEWS[0]);

  const root = panesRoot();
  const shell = paneFor(other);
  if (!root || !shell || paneFilled(shell) || inflight[other]) return;

  fetchPane(root, other);
}

function savedView(): string {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch (_e) {
    saved = null;
  }
  return saved && VIEWS.includes(saved) ? saved : "";
}

export function initViewToggle(): void {
  const seg = document.querySelector<HTMLElement>("[data-viewtoggle]");
  if (!seg) return;

  const rendered = panesRoot()?.dataset.candidatesView || "";
  const view = savedView() || seg.dataset.defaultView || VIEWS[0];
  persist(view);
  showView(view, VIEWS.includes(rendered) ? rendered : "");

  // Bind click handlers once (the header/seg is not re-rendered by async loads).
  if (seg.dataset.viewtoggleBound === "true") return;
  seg.dataset.viewtoggleBound = "true";

  // Warm the pane the viewer is about to ask for (see prefetchOtherPane).
  seg.addEventListener("pointerenter", prefetchOtherPane);
  seg.addEventListener("focusin", prefetchOtherPane);

  seg.querySelectorAll<HTMLElement>("button[data-view]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = btn.dataset.view || VIEWS[0];
      const current = seg.querySelector<HTMLElement>("button[data-view].active")?.dataset.view || "";
      if (next === current) return;
      persist(next);
      showView(next, current);
    });
  });
}
